import { baseService } from "./base";

// ============================================
// INTERNAL USERS (Staff, Admin, etc.)
// ============================================

export interface InternalUserWithSessionInfo {
  id: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  role: string;
  createdAt: string;
  sessionCount: number;
  lastLoginAt: string | null;
  isCurrentlyActive: boolean;
}

export interface UserSession {
  id: string;
  userId: string;
  loginAt: string;
  logoutAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  isActive: boolean;
  createdAt: string;
  user: {
    id: string;
    username: string;
    email: string | null;
    role: string;
  };
}

// ============================================
// SAAS CUSTOMERS (Clients)
// ============================================

export enum SubscriptionTier {
  FREE = "free",
  BASIC = "basic",
  PREMIUM = "premium",
  ENTERPRISE = "enterprise",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELLED = "cancelled",
  EXPIRED = "expired",
  TRIAL = "trial",
}

export interface SaasCustomer {
  id: string;
  companyName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  contactPhone: string | null;
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  monthlyServiceSpend: number;
  isSaasCustomer: boolean;
  createdAt: string;
  user: {
    id: string;
    username: string;
  } | null;
  totalPayments: number;
  lastPaymentDate: string | null;
}

export interface UpdateClientSubscriptionRequest {
  subscriptionTier?: SubscriptionTier;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  monthlyServiceSpend?: number;
}

// ============================================
// PAYMENTS
// ============================================

export enum SaasPaymentType {
  SUBSCRIPTION = "subscription",
  MANUAL = "manual",
  SERVICE_CREDIT = "service_credit",
}

export interface SaasPayment {
  id: string;
  clientId: string;
  type: SaasPaymentType;
  amount: number;
  description: string | null;
  paymentDate: string;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  notes: string | null;
  createdAt: string;
  client: {
    id: string;
    companyName: string;
    email: string | null;
  };
}

export interface CreatePaymentRequest {
  clientId: string;
  type: SaasPaymentType;
  amount: number;
  description?: string;
  paymentDate: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  notes?: string;
}

export interface UpdatePaymentRequest {
  type?: SaasPaymentType;
  amount?: number;
  description?: string;
  paymentDate?: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  notes?: string;
}

// ============================================
// DASHBOARD
// ============================================

export interface DashboardStats {
  internalUsersCount: number;
  activeSessionsCount: number;
  saasCustomersCount: number;
  payingCustomersCount: number;
  totalPaymentsAmount: number;
  recentSessions: UserSession[];
}

// ============================================
// API SERVICE
// ============================================

export const saasControlService = {
  // Dashboard
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await baseService.get("/saas-control/dashboard");
    return response.data;
  },

  // Internal Users (Staff, Admin, etc.)
  getInternalUsers: async (): Promise<InternalUserWithSessionInfo[]> => {
    const response = await baseService.get("/saas-control/internal-users");
    return response.data;
  },

  // Sessions
  getSessions: async (userId?: string): Promise<UserSession[]> => {
    const url = userId
      ? `/saas-control/sessions?userId=${userId}`
      : "/saas-control/sessions";
    const response = await baseService.get(url);
    return response.data;
  },

  // SaaS Customers (Clients)
  getSaasCustomers: async (): Promise<SaasCustomer[]> => {
    const response = await baseService.get("/saas-control/customers");
    return response.data;
  },

  // Get all clients for payment dropdown
  getAllClientsForPayment: async (): Promise<{ id: string; companyName: string; email: string | null }[]> => {
    const response = await baseService.get("/saas-control/customers/all");
    return response.data;
  },

  updateClientSubscription: async (
    clientId: string,
    data: UpdateClientSubscriptionRequest
  ): Promise<SaasCustomer> => {
    const response = await baseService.put(
      `/saas-control/customers/${clientId}/subscription`,
      data
    );
    return response.data;
  },

  // Payments
  getPayments: async (clientId?: string): Promise<SaasPayment[]> => {
    const url = clientId
      ? `/saas-control/payments?clientId=${clientId}`
      : "/saas-control/payments";
    const response = await baseService.get(url);
    return response.data;
  },

  createPayment: async (data: CreatePaymentRequest): Promise<SaasPayment> => {
    const response = await baseService.post("/saas-control/payments", data);
    return response.data;
  },

  updatePayment: async (
    id: string,
    data: UpdatePaymentRequest
  ): Promise<SaasPayment> => {
    const response = await baseService.put(
      `/saas-control/payments/${id}`,
      data
    );
    return response.data;
  },

  deletePayment: async (id: string): Promise<{ message: string }> => {
    const response = await baseService.delete(`/saas-control/payments/${id}`);
    return response.data;
  },
};
